/* app/providers/WorkspaceTabSync.jsx — пространство, выбранное в СОСЕДНЕЙ вкладке, и сессия
   браузера, общая для вкладок (kitchen-ux A2 круги 2–5, S10).

   localStorage['restorium.active_workspace_id'] — общий для вкладок «последний выбор»;
   X-Workspace-Id вкладка шлёт СВОЙ (lib/api/client.js). Другая вкладка переключила
   пространство → событие storage здесь → вкладка переходит тем же путём, что и своё
   переключение (AuthProvider.adoptWorkspace → перемонтирование экрана по activeWorkspace),
   но через сторож несохранённого: грязная форма → диалог с причиной; «Отмена» — вкладка
   остаётся в своём пространстве (заголовок прежний, выбор той вкладки не трогаем).
   Ставится в App.jsx внутри UnsavedProvider (нужны и useAuth, и useUnsaved).

   Когда переходить (круги 3–5). Вкладка занята, пока:
   · открыт диалог (lib/dialogHold.js): поток «прочитал → спросил → записал» ждёт ответа, сеть
     в это время тихая, а подтверждённая запись должна уйти туда, где читали;
   · идут запросы: переход — после 300 мс тишины сети, иначе хвост операции (поставщик → цена,
     строки ТТК, стрим ответа чата) ушёл бы с новым X-Workspace-Id. Опросы только на чтение
     идут вне счёта (QUIET_POLL) и переход не держат.
   Дольше 30 с — постоянный тост «Здесь ещё идёт загрузка» с «Переключить сейчас» / «Остаться
   здесь»; загрузка кончилась после этого — не переключаемся молча (пришедший ответ исчез бы с
   экрана), а спрашиваем «Загрузка завершилась… Переключить?».
   Непосредственно перед переходом — синхронно: с момента тишины не начался ни один запрос и не
   открылся диалог, форма чистая. Новое событие storage отменяет прежнее ожидание (слушатели и
   тост снимаются); вопрос, уже заданный пользователю, доводит до последнего выбора сам.
   Грязная форма: вопрос — сразу, как закроются другие диалоги (поверх подтверждения не
   спрашиваем), даже если сеть занята своим «Сохранить» (кнопки «Сохраняется…»); переход после
   ответа — в том же вопросе, когда сеть затихнет. Долгие ожидания чистой вкладки идут ВНЕ
   сторожа: пока его действие выполняется, navigate() приложения обходит проверку.
   Пока переход ждёт (цель доступна), признак tabSwitchPending() в lib/api/client.js (хук —
   hooks/useTabSwitchPending.js) = { target, stage: 'wait' | 'dialog' }: реестры говорят по нему
   «вкладка переходит в другое пространство»; переход кончился, отменён или вкладку заблокировали — null.
   Кадры drawer-стека прежнего пространства (?edit=) при переходе снимает AuthProvider.

   Сессия браузера общая (круги 3–5, T7, T17): в соседней вкладке могли войти ДРУГИМ
   пользователем — вкладка писала бы от его имени (рецепт — в чужой аккаунт или в общее
   пространство под чужим именем). «Кто я» (/api/auth/me/) сверяется:
   · сразу на каждое событие общего выбора и на МАРКЕР сессии (restorium.session_marker — вход,
     выход и загрузка под другим пользователем переписывают его новым значением; событие
     приходит и тогда, когда общий выбор не менялся: вход в то же пространство 20 → 20);
   · при возвращении во вкладку (focus / visibilitychange, видима): заблокированная — всегда,
     незаблокированная — не чаще раза в 20 с (сессия могла смениться тихо: истекла, вошли
     другим мимо этой вкладки);
   · прямо перед переходом, если прошлой сверке больше 0,5 с;
   · на ответ 409 workspace_unavailable (+ свежий список пространств).
   Пока сверка идёт, небезопасные запросы ждут её вердикта (client.js). Пользователь другой —
   вкладка блокируется экраном «вход под другим пользователем», правка не отправляется (замок
   сессии); сессии нет — «Вы вышли в другой вкладке». Прежний пользователь вошёл снова — блок
   снимается, несохранённая правка на месте. Сеть не ответила — ни блока, ни перехода. */

import React from 'react';
import { useAuth } from './AuthProvider.jsx';
import { useUnsaved } from './UnsavedChangesProvider.jsx';
import { useToast } from './ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import * as authApi from '../../lib/api/auth.js';
import {
  SESSION_MARKER_KEY, WORKSPACE_STORAGE_KEY, beginSessionCheck, getActiveWorkspaceId, getStoredWorkspaceId,
  onWorkspaceUnavailable, requestsInFlight, requestsStarted, setSessionLock, setTabSwitchPending, whenNetworkQuiet,
} from '../../lib/api/client.js';
import { dialogsOpen, whenNoDialogs } from '../../lib/dialogHold.js';
import { clearServerRegistryCache } from '../../lib/table/serverRegistryCache.js';
import { workspaceName } from '../../lib/workspace.js';

  const { useEffect, useRef, useState } = React;
  const QUIET_MS = 300;
  const SLOW_MS = 30000;                  // потолок молчаливого ожидания загрузки
  const FOCUS_CHECK_MS = 20000;           // «кто я» при возвращении незаблокированной вкладки — не чаще
  const ME_TIMEOUT_MS = 10000;            // потолок сверки «кто я»: дольше — «неизвестно», запись вкладки не ждёт
  const ME_FRESH_MS = 500;                // сверка моложе — перед переходом не повторяется

  // имя — как в переключателе: личное — «Моё пространство», а не ФИО владельца
  const nameOf = (list, id) => {
    const w = (list || []).find((x) => x.id === id);
    return w ? (workspaceName(w) || null) : null;
  };

  /* Кто сейчас в сессии браузера → 'same' | 'other' | 'out' | 'unknown' (сеть/сервер не
     ответили — ничего не решаем, вкладка остаётся как есть). Потолок limitMs (круг 6): пока /me
     не ответил, запись вкладки ждёт сверку (гейт client.js) — не дольше потолка; дальше запрос
     отменяется, вердикт «неизвестно»: гейт открыт, блока нет. */
  async function whoIsInSession(userId, limitMs = ME_TIMEOUT_MS) {
    let r;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    let timer = null;
    const late = new Promise((resolve) => {
      timer = setTimeout(() => { if (ctrl) ctrl.abort(); resolve(null); }, limitMs);
    });
    // сверка — не часть операции экрана: вне счёта запросов в полёте (иначе сама отодвигала
    // бы тишину сети, которой ждёт переход)
    try {
      r = await Promise.race([authApi.me({ track: false, ...(ctrl ? { signal: ctrl.signal } : {}) }), late]);
    } catch (e) {
      return 'unknown';
    } finally {
      clearTimeout(timer);
    }
    if (r == null) return 'unknown';                                 // не ответил за потолок
    if (r && r.ok && r.data && r.data.id != null) return String(r.data.id) === String(userId) ? 'same' : 'other';
    if (r && (r.status === 401 || r.status === 403)) return 'out';
    return 'unknown';
  }

  // тексты — не про «несохранённые изменения»: это не диалог сторожа (там — «Сохранить/Отбросить»)
  const BLOCK_TEXT = {
    'other-user': {
      title: 'В этом браузере выполнен вход под другим пользователем',
      body: (who) => `Эта вкладка открыта под ${who ? `«${who}»` : 'прежним пользователем'}. Чтобы не записать данные `
        + 'в чужой аккаунт, работа в ней остановлена — правки этой вкладки никуда не отправлены.',
    },
    'logged-out': {
      title: 'Вы вышли в другой вкладке',
      body: () => 'Сессия этого браузера завершена — правки этой вкладки никуда не отправлены. '
        + 'Если снова войти тем же пользователем, вкладка продолжит работу с того же места.',
    },
  };

  // экран блокировки: без крестика и закрытия по фону — только обновить страницу или войти
  function SessionChangedScreen({ reason, who }) {
    const t = BLOCK_TEXT[reason] || BLOCK_TEXT['other-user'];
    const out = reason === 'logged-out';
    // Экран сменился поверх открытого окна («вышли» → «вход под другим пользователем» и обратно):
    // главная кнопка другая, а начальный фокус окна уже отработал на прежней — перевести фокус на
    // новую главную (QA Г3, I06). Первый показ — фокус ставит само окно (autoFocusSelector).
    const shownRef = useRef(reason);
    useEffect(() => {
      if (shownRef.current === reason) return;
      shownRef.current = reason;
      const doc = typeof document !== 'undefined' ? document : null;
      const el = doc && typeof doc.querySelector === 'function' ? doc.querySelector('[data-session-primary]') : null;
      if (el && typeof el.focus === 'function') el.focus();
    }, [reason]);
    return (
      <Modal open size="sm" role="alertdialog" zIndex="var(--z-max)" closeOnBackdrop={false}
        labelledBy="session-changed-title" describedBy="session-changed-body" autoFocusSelector="[data-session-primary]"
        footer={<>
          <Button variant={out ? 'secondary' : 'primary'} data-session-primary={out ? undefined : true}
            onClick={() => window.location.reload()}>Обновить страницу</Button>
          <Button variant={out ? 'primary' : 'secondary'} data-session-primary={out ? true : undefined}
            onClick={() => window.location.assign('/signin')}>Войти</Button>
        </>}>
        {/* тело — описание окна (первый показ читается целиком); причина сменилась поверх открытого
            окна — живая область объявляет её целиком: перевод фокуса читает только кнопку
            (верификатор QA-r1-B). Окно не пересоздаём — возврат фокуса после него прежний. */}
        <div className="stack stack-8" aria-live="assertive" aria-atomic="true">
          <h2 className="t-h3" id="session-changed-title">{t.title}</h2>
          <div className="t-body text-dim" id="session-changed-body">{t.body(who)}</div>
        </div>
      </Modal>
    );
  }

  function WorkspaceTabSync({ slowAfterMs = SLOW_MS, focusCheckMs = FOCUS_CHECK_MS, meTimeoutMs = ME_TIMEOUT_MS }) {
    const auth = useAuth();
    const unsaved = useUnsaved();
    const toast = useToast();
    const [blocked, setBlocked] = useState(null);
    const ref = useRef(null);
    ref.current = { auth, unsaved, toast, slowAfterMs, focusCheckMs, meTimeoutMs };
    const blockedRef = useRef(null);
    // текущий переход: { ctrl, stage: 'wait' | 'dialog', target, ws, slowShown, toastId, decided, choose, decision }
    const flowRef = useRef(null);
    const lastSameRef = useRef(0);        // когда «кто я» последний раз подтвердила того же пользователя
    const focusCheckRef = useRef(0);      // когда была сверка по возвращению во вкладку (троттлинг)
    const checkingRef = useRef(false);    // сверка по возвращению идёт
    const unavailRef = useRef(false);     // разбор 409 workspace_unavailable идёт

    const wsName = (id) => nameOf(ref.current && ref.current.auth && ref.current.auth.workspaces, id);
    const say = (text, tone = 'muted') => {
      const t = ref.current && ref.current.toast;
      if (t && typeof t.show === 'function') t.show(text, tone);
    };
    // постоянный тост перехода с действиями; flow.toastId — чтобы снять
    const dropToast = (flow) => {
      const t = ref.current && ref.current.toast;
      if (flow.toastId != null && t && typeof t.dismiss === 'function') t.dismiss(flow.toastId);
      flow.toastId = null;
    };
    const offerToast = (flow, kind) => {
      const t = ref.current && ref.current.toast;
      if (!t || typeof t.show !== 'function') return;
      dropToast(flow);
      const name = wsName(getStoredWorkspaceId());
      const where = name ? `Пространство «${name}» выбрано в другой вкладке` : 'В другой вкладке выбрано другое пространство';
      const text = kind === 'slow' ? `${where}. Здесь ещё идёт загрузка.` : `Загрузка завершилась. ${where}.`;
      flow.toastId = t.show(
        <span className="stack stack-4">
          <span>{text}</span>
          <span className="cluster cluster-2">
            <Button size="sm" variant="primary" onClick={() => flow.choose('switch')}>
              {kind === 'slow' ? 'Переключить сейчас' : 'Переключить'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => flow.choose('stay')}>Остаться здесь</Button>
          </span>
        </span>,
        'muted', 0,
      );
    };


    // прежний переход, который ещё ждёт (диалога, тишины) или сверяется, — отменить: его
    // слушатели, таймеры и тост снимаются. Тот, что уже спросил пользователя (stage 'dialog'), —
    // не трогаем: ответ доведёт до последнего выбора сам.
    const cancelWaiting = () => {
      const f = flowRef.current;
      if (f && f.stage !== 'dialog') { f.ctrl.abort(); dropToast(f); flowRef.current = null; setTabSwitchPending(null); }
    };
    // сессия браузера сменилась — снимки серверных реестров этой вкладки прочь: их строки
    // прежнего пользователя не показываются ни следующему, ни после «входа» тем же (S10)
    const block = (reason) => {
      blockedRef.current = reason; setSessionLock(reason); clearServerRegistryCache(); setBlocked(reason); cancelWaiting();
    };
    const unblock = () => { blockedRef.current = null; setSessionLock(null); setBlocked(null); };
    // сверка сессии: true — тот же пользователь; иначе вкладка заблокирована или ничего не решено.
    // Пока идёт — небезопасные запросы ждут вердикта (снимаем после block: ждавшие видят замок).
    const sessionIsMine = async () => {
      const a = ref.current && ref.current.auth;
      if (!a || !a.user) return false;
      const done = beginSessionCheck();
      try {
        const who = await whoIsInSession(a.user.id, ref.current.meTimeoutMs);
        if (who === 'other') { block('other-user'); return false; }
        if (who === 'out') { block('logged-out'); return false; }
        if (who === 'same') lastSameRef.current = Date.now();
        return who === 'same';
      } finally {
        done();
      }
    };
    // заблокированная вкладка: вернулся ли прежний пользователь
    const recheckBlocked = async () => {
      const a = ref.current && ref.current.auth;
      if (!a || !a.user || !blockedRef.current) return;
      const who = await whoIsInSession(a.user.id, ref.current.meTimeoutMs);
      if (!blockedRef.current) return;
      if (who === 'same') unblock();
      else if (who === 'other') block('other-user');
      else if (who === 'out') block('logged-out');
    };

    // возвращение во вкладку (focus / visibilitychange, видима): заблокированная — сверка всегда;
    // незаблокированная — не чаще раза в focusCheckMs и не поверх идущей (сессия могла смениться
    // тихо — без единого события storage)
    useEffect(() => {
      if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined;
      const doc = typeof document !== 'undefined' ? document : null;
      const onReturn = () => {
        if (doc && doc.visibilityState === 'hidden') return;
        if (blockedRef.current) { recheckBlocked(); return; }
        const a = ref.current && ref.current.auth;
        if (!a || !a.user || checkingRef.current) return;
        const now = Date.now();
        if (now - focusCheckRef.current < ref.current.focusCheckMs) return;
        focusCheckRef.current = now;
        checkingRef.current = true;
        sessionIsMine().finally(() => { checkingRef.current = false; });
      };
      window.addEventListener('focus', onReturn);
      if (doc && typeof doc.addEventListener === 'function') doc.addEventListener('visibilitychange', onReturn);
      return () => {
        window.removeEventListener('focus', onReturn);
        if (doc && typeof doc.removeEventListener === 'function') doc.removeEventListener('visibilitychange', onReturn);
      };
      // eslint-disable-next-line
    }, []);

    // 409 workspace_unavailable (сервер отказал в записи в недоступное пространство): «кто я»
    // (другой пользователь / вышли — блок) и свежий список — без переключения: правка на экране,
    // пользователь решает сам
    useEffect(() => onWorkspaceUnavailable(async () => {
      if (unavailRef.current || blockedRef.current) return;
      unavailRef.current = true;
      try {
        if (!(await sessionIsMine())) return;
        const a = ref.current && ref.current.auth;
        const id = getActiveWorkspaceId();
        const ws = a && typeof a.findWorkspace === 'function' ? await a.findWorkspace(id, { fresh: true }) : null;
        const name = (ws && workspaceName(ws)) || wsName(id);
        const label = name ? `«${name}»` : 'этой вкладки';
        say(ws
          ? `Не записано: пространство ${label} сейчас недоступно. Попробуйте ещё раз.`
          : `Пространство ${label} больше недоступно — изменения не записаны. Выберите другое пространство.`, 'danger');
      } finally {
        unavailRef.current = false;
      }
      // eslint-disable-next-line
    }), []);

    useEffect(() => {
      if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined;
      const live = (flow) => flowRef.current === flow && !flow.ctrl.signal.aborted;
      const isDirty = () => {
        const g = ref.current && ref.current.unsaved;
        return !!(g && typeof g.isAnyDirty === 'function' && g.isAnyDirty());
      };
      const newFlow = () => {
        const flow = {
          ctrl: new AbortController(), stage: 'wait', target: null, ws: null,
          slowShown: false, toastId: null, decided: null, choose: null, decision: null,
        };
        // решение по тосту: 'switch' — переходить, не дожидаясь тишины; 'stay' — остаться (ожидание
        // кончается ответом: гонка в idle и вопрос по концу загрузки возвращают «не переходить»)
        flow.decision = new Promise((resolve) => {
          flow.choose = (c) => {
            if (flow.decided) return;
            flow.decided = c;
            dropToast(flow);
            resolve(c);
          };
        });
        return flow;
      };
      // диалоги закрыты, затем сеть затихла (диалог, открытый за время тишины, ловит синхронная
      // проверка перед переходом). dialogs:false — внутри своего вопроса (открыт сам диалог сторожа).
      // Дольше потолка — тост с действиями; тишина пришла после него — вопрос, а не переход молча.
      const idle = async (flow, { dialogs }) => {
        if (dialogs && !(await whenNoDialogs({ signal: flow.ctrl.signal }))) return false;
        const wait = new AbortController();
        const stop = () => wait.abort();
        flow.ctrl.signal.addEventListener('abort', stop);
        const quiet = whenNetworkQuiet(QUIET_MS, {
          signal: wait.signal,
          slowAfterMs: ref.current.slowAfterMs,
          onSlow: () => { if (!flow.slowShown && !flow.decided) { flow.slowShown = true; offerToast(flow, 'slow'); } },
        }).then((q) => (q ? 'quiet' : 'cancel'));
        const r = await Promise.race([quiet, flow.decision]);
        wait.abort();                                            // победило решение — снять ожидание тишины
        flow.ctrl.signal.removeEventListener('abort', stop);
        if (r === 'cancel' || r === 'stay' || flow.ctrl.signal.aborted) return false;
        if (r === 'switch') return true;
        if (flow.slowShown && !flow.decided) {
          offerToast(flow, 'done');
          const c = await flow.decision;
          return c === 'switch' && !flow.ctrl.signal.aborted;
        }
        return true;
      };
      // попытка перехода: тишина → «кто я» (если прошлой сверке больше ME_FRESH_MS) и доступность
      // ПОСЛЕДНЕГО выбора (та вкладка могла переключиться ещё раз) → синхронно: с тишины ничего не
      // началось и не открылось, форма чистая → переход. «Переключить сейчас» — без условия
      // тишины (запросы в полёте уже ушли со своим id), но не поверх открытого диалога.
      // → 'done' | 'stop' | 'dirty'
      const attempt = async (flow, { dialogs }) => {
        for (;;) {
          if (!(await idle(flow, { dialogs }))) return 'stop';
          const mark = requestsStarted();
          // заблокированную вкладку ожидание вне вопроса снимает сам блок, а в вопросе — сверка ниже
          if (!live(flow)) return 'stop';
          const target = getStoredWorkspaceId();
          if (target == null || target === getActiveWorkspaceId()) return 'stop';
          if (Date.now() - lastSameRef.current > ME_FRESH_MS) {
            if (!(await sessionIsMine())) return 'stop';
            if (!live(flow)) return 'stop';
          }
          const a = ref.current.auth;
          const ws = typeof a.findWorkspace === 'function' ? await a.findWorkspace(target) : null;
          if (!live(flow) || !ws) return 'stop';
          const forced = flow.decided === 'switch';
          if ((!forced && (requestsStarted() !== mark || requestsInFlight() > 0))
            || (dialogs && dialogsOpen() > 0) || getStoredWorkspaceId() !== target) continue;
          if (isDirty()) { flow.target = target; flow.ws = ws; return 'dirty'; }
          const ok = await a.adoptWorkspace(target);             // цель в списке — без запроса, синхронно
          if (ok) {
            const name = workspaceName(ws) || wsName(target);
            say(name ? `Пространство «${name}» выбрано в другой вкладке — эта вкладка переключена` : 'Пространство переключено в другой вкладке');
          }
          return ok ? 'done' : 'stop';
        }
      };
      const run = async (flow, next) => {
        const a = ref.current.auth;
        // доступно ли пространство этой вкладке — ДО вопроса (T9): недоступное — ни вопроса, ни перехода
        const known = (a.workspaces || []).find((w) => w.id === next) || null;
        const targetWs = known || (typeof a.findWorkspace === 'function' ? await a.findWorkspace(next) : null);
        if (!live(flow) || !targetWs) return;
        // переход ожидает (client.js): цель доступна — реестры могут сказать об этом словами
        setTabSwitchPending({ target: next, stage: 'wait' });
        for (;;) {
          // поверх открытого диалога не спрашиваем и не переходим; ждали — «кто я» заново
          const waited = dialogsOpen() > 0;
          if (!(await whenNoDialogs({ signal: flow.ctrl.signal }))) return;
          if (waited && !(await sessionIsMine())) return;
          if (!live(flow)) return;
          let r = 'dirty';
          if (!isDirty()) r = await attempt(flow, { dialogs: true });
          if (r !== 'dirty' || !live(flow)) return;
          // есть что сохранять — вопрос сразу, даже если сеть занята своим «Сохранить»
          // (кнопки «Сохраняется…»); ответ «Сохранить / Отбросить» — переход в том же вопросе
          const g = ref.current.unsaved;
          if (!g || typeof g.guard !== 'function') return;
          const target = flow.target != null ? flow.target : getStoredWorkspaceId();
          const there = workspaceName(flow.ws || targetWs) || wsName(target);
          const here = wsName(getActiveWorkspaceId());
          const reason = `В другой вкладке выбрано пространство${there ? ` «${there}»` : ''}. `
            + `Если отменить, эта вкладка останется ${here ? `в «${here}»` : 'в текущем'}.`;
          let again = false;
          flow.stage = 'dialog';
          setTabSwitchPending({ target, stage: 'dialog' });
          await g.guard(async () => {
            const r2 = await attempt(flow, { dialogs: false });
            if (r2 === 'dirty') again = true;                     // пока ждали, форму поправили — спросить заново
            return r2 === 'done';
          }, { reason });
          flow.stage = 'wait';
          if (!again || !live(flow)) return;
          setTabSwitchPending({ target: getStoredWorkspaceId(), stage: 'wait' });
        }
      };
      const onStorage = async (e) => {
        if (!e) return;
        const { auth: a } = ref.current || {};
        if (e.key === SESSION_MARKER_KEY) {
          // вход / выход / загрузка под другим пользователем в соседней вкладке — сверить сессию
          if (!a || !a.user) return;
          if (blockedRef.current) { await recheckBlocked(); return; }
          await sessionIsMine();
          return;
        }
        if (e.key !== WORKSPACE_STORAGE_KEY) return;
        if (!a || !a.user || typeof a.adoptWorkspace !== 'function') return;
        if (blockedRef.current) { await recheckBlocked(); return; }
        // общий выбор изменился — «кто я» сразу и на каждое событие (круг 4): вошли другим
        // пользователем (в т. ч. в общее пространство) — блок до всякого ожидания и вопроса
        if (!(await sessionIsMine())) return;
        const next = getStoredWorkspaceId();
        const mine = getActiveWorkspaceId();
        if (next == null || next === mine) { cancelWaiting(); return; }
        const prev = flowRef.current;
        if (prev && prev.stage === 'dialog') {                   // вопрос задан — ответ доведёт сам
          setTabSwitchPending({ target: next, stage: 'dialog' });
          return;
        }
        cancelWaiting();
        const flow = newFlow();
        flowRef.current = flow;
        try {
          await run(flow, next);
        } finally {
          // тост к этому времени снят: ответом (choose), заменой вопросом, отменой ожидания или
          // размонтированием — переход не кончается с тостом на экране
          if (flowRef.current === flow) { flowRef.current = null; setTabSwitchPending(null); }
        }
      };
      window.addEventListener('storage', onStorage);
      return () => {
        window.removeEventListener('storage', onStorage);
        const f = flowRef.current;                                 // размонтирование — любой переход снять
        if (f) { f.ctrl.abort(); dropToast(f); flowRef.current = null; }
        setTabSwitchPending(null);
      };
      // eslint-disable-next-line
    }, []);

    if (!blocked) return null;
    const user = auth && auth.user;
    return <SessionChangedScreen reason={blocked} who={user ? (user.full_name || user.email || null) : null} />;
  }

export { WorkspaceTabSync, SessionChangedScreen };
